type SharedReveal = {
  value: string | null;
  inFlight: Promise<string | null> | null;
  refs: number;
};

// Shared per event so one screen writes one audit row; dropped once no instance is mounted.
const shared = new Map<string, SharedReveal>();

const entryFor = (eventId: string): SharedReveal => {
  const existing = shared.get(eventId);
  if (existing) return existing;
  const created: SharedReveal = { value: null, inFlight: null, refs: 0 };
  shared.set(eventId, created);
  return created;
};

export const retainSharedReveal = (eventId: string): (() => void) => {
  const entry = entryFor(eventId);
  entry.refs += 1;
  return () => {
    entry.refs -= 1;
    if (entry.refs <= 0 && shared.get(eventId) === entry) shared.delete(eventId);
  };
};

export const readSharedReveal = (eventId: string): string | null => {
  const entry = shared.get(eventId);
  return entry?.value ?? null;
};

export const clearSharedReveal = (eventId: string): void => {
  const entry = shared.get(eventId);
  if (!entry) return;
  entry.value = null;
  entry.inFlight = null;
};

export const runSharedReveal = (eventId: string, start: () => Promise<string | null>): Promise<string | null> => {
  const entry = shared.get(eventId);
  if (!entry) return start();
  if (entry.inFlight) return entry.inFlight;

  const promise = start()
    .then(value => {
      // Null is a failure and never cached so a retry reaches the server; hide()/password change drop the claim.
      if (value !== null && shared.get(eventId) === entry && entry.inFlight === promise) {
        entry.value = value;
      }
      return value;
    })
    .finally(() => {
      if (entry.inFlight === promise) entry.inFlight = null;
    });

  entry.inFlight = promise;
  return promise;
};

// source null = a change from outside any hook instance, so every instance watching the event re-masks.
type PasswordChangeListener = (eventId: string, source: symbol | null) => void;

const listeners = new Set<PasswordChangeListener>();

// The broadcast never carries the plaintext.
export const notifyPasswordChanged = (eventId: string, source: symbol | null): void => {
  clearSharedReveal(eventId);
  listeners.forEach(listener => listener(eventId, source));
};

export const subscribeToPasswordChanges = (listener: PasswordChangeListener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
